import { useCallback, useEffect } from 'react';
import { Analytics } from '../analytics';
import { isPlaygroundSurface } from '../config/deployment';
import { useOptionalConfig } from '../config/unified';
import { ChatStorageService } from '../services/chatStorage';

export interface ContentRecommendationData {
    title: string;
    url: string;
    category: 'article' | 'case_study' | 'video' | 'document' | 'product_page' | 'tool';
    description?: string;
    is_internal: boolean;
    og_image_url?: string | null;
}

interface ContentRecommendationCardProps {
    recommendation: ContentRecommendationData;
    primaryColor?: string;
    onClick?: () => void;
}

function getDomain(url: string): string {
    try {
        return new URL(url).hostname.replace(/^www\./, '');
    } catch {
        return url;
    }
}

/**
 * Slice the JSON object that follows a `📎` marker by tracking brace depth.
 *
 * The marker used to have to be the last thing in the message, because everything
 * after it was assumed to be the JSON. Scanning to the matching close brace instead
 * lets the model keep writing afterwards — which is what puts the 👉 question below
 * the card, next to the chips that answer it. Quoted braces don't count.
 */
function sliceMarkerJson(afterMarker: string): { json: string; rest: string } | null {
    const start = afterMarker.indexOf('{');
    if (start === -1) return null;

    let depth = 0;
    let inString = false;
    let escaped = false;

    for (let i = start; i < afterMarker.length; i++) {
        const char = afterMarker[i];

        if (escaped) {
            escaped = false;
        } else if (char === '\\') {
            escaped = true;
        } else if (char === '"') {
            inString = !inString;
        } else if (!inString && char === '{') {
            depth++;
        } else if (!inString && char === '}') {
            depth--;
            if (depth === 0) {
                return {
                    json: afterMarker.substring(start, i + 1),
                    rest: afterMarker.substring(i + 1),
                };
            }
        }
    }

    // Unbalanced — still streaming in.
    return null;
}

/**
 * parseContentRecommendation — extracts a content recommendation from an
 * assistant message that uses the `📎{json}` marker contract.
 *
 * The marker and its JSON are always stripped from the displayed content (even if
 * parsing fails, so visitors never see raw marker syntax). Anything the model writes
 * after the JSON comes back as `trailingContent` and renders below the card.
 */
export function parseContentRecommendation(content: string): {
    mainContent: string;
    trailingContent: string;
    recommendation: ContentRecommendationData | null;
} {
    const markerIndex = content.lastIndexOf('📎');
    if (markerIndex === -1)
        return { mainContent: content, trailingContent: '', recommendation: null };

    const mainContent = content.substring(0, markerIndex).trim();
    const afterMarker = content.substring(markerIndex + '📎'.length);
    const sliced = sliceMarkerJson(afterMarker);

    if (sliced) {
        try {
            const parsed = JSON.parse(sliced.json);
            if (parsed && typeof parsed.title === 'string' && typeof parsed.url === 'string') {
                return {
                    mainContent,
                    trailingContent: sliced.rest.trim(),
                    recommendation: parsed as ContentRecommendationData,
                };
            }
        } catch {
            // Balanced but not valid JSON — hide the marker, no card
        }
    }

    return { mainContent, trailingContent: '', recommendation: null };
}

function ContentRecommendationCard({ recommendation, onClick }: ContentRecommendationCardProps) {
    const handleClick = () => {
        onClick?.();
        // Only open http/https URLs to prevent javascript: or data: injection
        try {
            const parsed = new URL(recommendation.url);
            if (parsed.protocol === 'https:' || parsed.protocol === 'http:') {
                window.open(recommendation.url, '_blank', 'noopener,noreferrer');
            }
        } catch {
            // Invalid URL — do nothing
        }
    };

    return (
        // biome-ignore lint/a11y/useSemanticElements: div with role="link" is used because an <a> would need an href which is set dynamically via onClick
        <div
            onClick={handleClick}
            role="link"
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === 'Enter') handleClick();
            }}
            className="rose-content-card"
            style={{
                marginTop: '8px',
                borderRadius: '10px',
                border: '1px solid var(--rw-border-chip)',
                backgroundColor: 'var(--rw-bg-chip)',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'row',
                animation: 'roseContentCardSlideUp 0.4s ease-out forwards',
                opacity: 0,
                transform: 'translateY(8px)',
            }}
        >
            {/* OG Image — square thumbnail on the left */}
            {recommendation.og_image_url && (
                <img
                    src={recommendation.og_image_url}
                    alt=""
                    style={{
                        width: '88px',
                        minHeight: '88px',
                        objectFit: 'cover',
                        display: 'block',
                        flexShrink: 0,
                    }}
                    onError={(e) => {
                        (e.currentTarget as HTMLImageElement).style.display = 'none';
                    }}
                />
            )}

            {/* Text content */}
            <div
                style={{
                    padding: '10px 12px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '2px',
                    minWidth: 0,
                    justifyContent: 'center',
                }}
            >
                {/* Domain */}
                <span
                    style={{
                        fontSize: '11px',
                        color: 'var(--rw-text-secondary)',
                        lineHeight: '1.3',
                    }}
                >
                    {getDomain(recommendation.url)}
                </span>

                {/* Title */}
                <span
                    style={{
                        fontWeight: 600,
                        fontSize: '13px',
                        lineHeight: '1.3',
                        color: 'var(--rw-text-primary)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical' as const,
                    }}
                >
                    {recommendation.title}
                </span>

                {/* Description */}
                {recommendation.description && (
                    <p
                        style={{
                            margin: 0,
                            fontSize: '12px',
                            color: 'var(--rw-text-secondary)',
                            lineHeight: '1.4',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical' as const,
                        }}
                    >
                        {recommendation.description}
                    </p>
                )}
            </div>

            <style>{`
                .rose-content-card:hover {
                    border-color: var(--rw-border-selection-accent) !important;
                    box-shadow: 0 2px 8px rgb(0 0 0 / 0.08);
                }

                .rose-content-card:active {
                    transform: translateY(1px) !important;
                }

                @keyframes roseContentCardSlideUp {
                    from {
                        opacity: 0;
                        transform: translateY(8px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                @media (prefers-reduced-motion: reduce) {
                    .rose-content-card {
                        animation: none !important;
                        opacity: 1 !important;
                        transform: none !important;
                    }
                }
            `}</style>
        </div>
    );
}

/**
 * ContentRecommendation — renders a content recommendation as either
 * an inline link (internal) or a rich card (external), with PostHog tracking.
 *
 * - Displayed: tracked once per URL via sessionStorage deduplication
 * - Clicked: tracked on every click
 *
 * Set `enableTracking={false}` for non-visitor contexts (e.g. the backoffice
 * timeline) so internal agent views don't pollute widget engagement metrics.
 */
export function ContentRecommendation({
    recommendation,
    primaryColor,
    enableTracking = true,
}: {
    recommendation: ContentRecommendationData;
    primaryColor?: string;
    enableTracking?: boolean;
}) {
    const resolver = useOptionalConfig()?.resolver ?? null;
    const overrideDomain = resolver?.domain ?? window.location.hostname;

    // Track displayed (once per URL per session)
    useEffect(() => {
        if (!enableTracking) return;
        const storageKey = `rw_content_rec_displayed:${recommendation.url}`;
        if (sessionStorage.getItem(storageKey)) return;
        sessionStorage.setItem(storageKey, 'true');
        Analytics.trackContentRecommendationDisplayed({
            title: recommendation.title,
            url: recommendation.url,
            category: recommendation.category,
            is_internal: recommendation.is_internal,
        });
    }, [
        enableTracking,
        recommendation.url,
        recommendation.title,
        recommendation.category,
        recommendation.is_internal,
    ]);

    const trackClick = useCallback(() => {
        if (!enableTracking) return;
        Analytics.trackContentRecommendationClicked({
            title: recommendation.title,
            url: recommendation.url,
            category: recommendation.category,
            is_internal: recommendation.is_internal,
        });
    }, [
        enableTracking,
        recommendation.title,
        recommendation.url,
        recommendation.category,
        recommendation.is_internal,
    ]);

    if (recommendation.is_internal) {
        // Validate URL protocol to prevent javascript:/data: XSS injection
        // since the URL originates from LLM-generated content
        let safeHref: string | undefined;
        try {
            const parsed = new URL(recommendation.url);
            if (parsed.protocol === 'https:' || parsed.protocol === 'http:') {
                safeHref = recommendation.url;
            }
        } catch {
            // Invalid URL — render as plain text
        }

        if (!safeHref) {
            return <span>{recommendation.title}</span>;
        }

        // On playground/preview surfaces the widget shares the origin with the preview
        // page, so navigating in place would replace the playground. Open in a new tab
        // there instead (mirrors MarkdownRenderer's same-origin handling).
        const navigateInPlace = !isPlaygroundSurface();

        return (
            <a
                href={safeHref}
                target={navigateInPlace ? '_self' : '_blank'}
                rel={navigateInPlace ? undefined : 'noopener noreferrer'}
                onClick={(e) => {
                    trackClick();
                    if (!navigateInPlace) return;
                    // Preserve modifier-clicks so users can open in a new tab/window
                    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                    // Set copilot-mode override so the widget reopens in copilot form
                    // factor on the destination page instead of appearing closed
                    ChatStorageService.setCopilotModeOverride(overrideDomain, true);
                }}
                style={{
                    color: 'var(--rw-link)',
                    textDecoration: 'underline',
                    display: 'inline',
                }}
            >
                {recommendation.title}
            </a>
        );
    }

    // External: render the rich card with click tracking
    return (
        <ContentRecommendationCard
            recommendation={recommendation}
            primaryColor={primaryColor}
            onClick={trackClick}
        />
    );
}
